import { useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'

import { formatAge, formatDecimal, formatInt, NO_VALUE, pctOf } from '../../lib/format.ts'
import type { IngestTotals, MetricsPayload } from '../../lib/sse/types.ts'
import { Panel } from '../primitives/Panel.tsx'
import { StatusDot } from '../primitives/StatusDot.tsx'

/** Roll length. Comfortably under the 2s tick, so a roll always settles first. */
const ROLL_MS = 620

/** Instant in, slow out: the moved digits snap to accent, then fade back. */
const FLASH_MS = 90
const FADE_MS = 520

function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  )
}

export interface RollInput {
  /** Last committed target, or null before the first figure arrives. */
  previous: number | null
  /** What is painted right now. Mid-roll this is NOT `previous`. */
  from: number | null
  /** The figure that just arrived on the wire. */
  value: number
  stale: boolean
  /** `stale` as of the previous commit. */
  wasStale: boolean
  monotonic: boolean
  reducedMotion: boolean
}

export interface RollDecision {
  /** The figure to display, after the monotonic shield. */
  target: number
  /** The display must jump rather than roll. */
  snap: boolean
  /** First index whose digit moved, or null when nothing should light up. */
  litFrom: number | null
}

/**
 * Every rule about when a total may move on screen, in one pure function.
 *
 * Pulled out of the effect deliberately: these are the traps the wire contract
 * warns about, and buried inside a `useEffect` they are neither readable nor
 * checkable. Here each one is a named term.
 */
export function decideRoll({
  previous,
  from,
  value,
  stale,
  wasStale,
  monotonic,
  reducedMotion,
}: RollInput): RollDecision {
  // The shield is dropped exactly when `stale` flips: applying it across that
  // flip would keep the partial figure as the floor and freeze the display on a
  // number that is millions short.
  const staleToggled = wasStale !== stale
  const target =
    monotonic && previous !== null && !staleToggled ? Math.max(previous, value) : value

  // Which digits moved, decided on the commit and never per frame: mid-roll
  // nearly every digit differs, and lighting all of them is just noise.
  const before = previous === null ? '' : formatInt(previous)
  const after = formatInt(target)
  let common = 0
  while (common < before.length && common < after.length && before[common] === after[common]) {
    common += 1
  }
  const litFrom = previous === null || before === after ? null : common

  /**
   * The last roll never reached its target, so the display is behind.
   *
   * `requestAnimationFrame` does not fire in a hidden tab: a console left in the
   * background keeps committing ticks while nothing paints, and the gap grows
   * for as long as it stays there. Rolling that gap out on return would spin
   * through everything the platform ingested in the meantime — the same false
   * count-up the confirmation jump has to avoid, earned a different way. Under
   * normal operation a roll finishes in 620ms against a 2s tick, so `from` lands
   * exactly on the previous target and this stays false.
   */
  const behind = previous !== null && from !== null && from !== previous

  /**
   * Snap instead of rolling when:
   *  - there is no previous figure to roll from — the first paint must not count
   *    up from zero;
   *  - the figure is partial: it sits well below the real one, and rolling it
   *    dresses an incomplete number as a live one;
   *  - the previous figure was partial. This is the confirmation tick, where the
   *    number leaps by everything the other instances counted — animating that
   *    leap is exactly the false count-up through billions to avoid;
   *  - the display is behind, per above;
   *  - it went backwards (hot store lost, restored from the database);
   *  - the operator asked for less motion.
   */
  const snap = from === null || stale || wasStale || behind || target < from || reducedMotion

  return { target, snap, litFrom }
}

interface RollingNumberProps {
  value: number | null
  /** The figure is partial. Suppresses the roll — see `decideRoll`. */
  stale: boolean
  /**
   * Shield the display against going backwards.
   *
   * For the all-time counters only. Losing the hot store and restoring from the
   * database can hand back a figure up to 60s old, and `Math.max` absorbs that
   * flicker — but `today_*` legitimately drops to zero at midnight UTC, and the
   * same shield there would pin yesterday's figure on screen until a reload.
   */
  monotonic?: boolean
  className?: string
}

/**
 * A cumulative total that rolls to its new value.
 *
 * `Metric` states that numbers do not animate, and that is right for a gauge: a
 * rolling counter on queue depth is noise. A cumulative total is the other case
 * — the *change* is the information, and the delta is a few tens of thousands
 * against billions, so the roll only ever moves the low digits and reads as an
 * odometer rather than as a number being invented on screen.
 *
 * Knowing when NOT to move is most of the work here.
 */
function RollingNumber({ value, stale, monotonic = false, className = '' }: RollingNumberProps) {
  const [shown, setShown] = useState<number | null>(value)
  const [lit, setLit] = useState(false)

  // Mid-roll the number on screen is not the payload's, so the committed target
  // is tracked apart from what is being displayed.
  const shownRef = useRef<number | null>(value)
  const committedRef = useRef<number | null>(value)
  const staleRef = useRef(stale)
  /** First index whose digit moved on the last commit; null = nothing to light. */
  const litFromRef = useRef<number | null>(null)
  const frameRef = useRef<number | null>(null)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (value === null) return

    const from = shownRef.current
    const { target, snap, litFrom } = decideRoll({
      previous: committedRef.current,
      from,
      value,
      stale,
      wasStale: staleRef.current,
      monotonic,
      reducedMotion: prefersReducedMotion(),
    })

    litFromRef.current = litFrom
    committedRef.current = target
    staleRef.current = stale

    if (frameRef.current !== null) cancelAnimationFrame(frameRef.current)
    if (timerRef.current !== null) clearTimeout(timerRef.current)

    // `from === null` already forces a snap; naming it here lets the roll
    // branch below be typed without an assertion.
    if (snap || from === null) {
      frameRef.current = null
      shownRef.current = target
      setShown(target)
    } else {
      const startedAt = performance.now()
      const step = (now: number): void => {
        const progress = Math.min(1, (now - startedAt) / ROLL_MS)
        // easeOutCubic: leaves fast, settles instead of stopping dead.
        const at = progress === 1 ? target : from + (target - from) * (1 - (1 - progress) ** 3)
        shownRef.current = at
        setShown(at)
        frameRef.current = progress === 1 ? null : requestAnimationFrame(step)
      }
      frameRef.current = requestAnimationFrame(step)
    }

    // Under reduced motion the fade is forced to 0ms globally, so lighting the
    // digits would blink rather than fade. Skip it there.
    if (litFrom !== null && !prefersReducedMotion()) {
      setLit(true)
      timerRef.current = setTimeout(() => setLit(false), FLASH_MS)
    }
  }, [value, stale, monotonic])

  useEffect(
    () => () => {
      if (frameRef.current !== null) cancelAnimationFrame(frameRef.current)
      if (timerRef.current !== null) clearTimeout(timerRef.current)
    },
    [],
  )

  if (shown === null) {
    return <span className={`live-value font-mono leading-none ${className}`}>{NO_VALUE}</span>
  }

  // Floor, not round: a counter that only grows must not tick down by one
  // because the roll's last frame rounded the other way.
  const text = formatInt(Math.floor(shown))
  const litFrom = litFromRef.current
  const litIndex = lit && litFrom !== null ? Math.min(litFrom, text.length) : text.length

  return (
    <span className={`live-value font-mono leading-none ${className}`}>
      {/* Split per character so only the digits that moved light up. The split
          is presentational, so it is hidden from assistive tech, which reads the
          whole figure from the copy beside it instead of digit by digit. */}
      <span aria-hidden="true">
        {[...text].map((char, index) => (
          <span
            key={index}
            style={{
              color: index >= litIndex ? 'var(--c-accent)' : undefined,
              transitionProperty: 'color',
              transitionDuration: lit ? '0ms' : `${FADE_MS}ms`,
              transitionTimingFunction: 'var(--ease-swift)',
            }}
          >
            {char}
          </span>
        ))}
      </span>
      <span className="sr-only">{text}</span>
    </span>
  )
}

/**
 * Freshness stamp for the totals.
 *
 * The same rule `ReferenceStamp` exists to apply: **marcar, nunca esconder**. A
 * partial total is still correct for this instance — what the operator needs is
 * to know it does not yet include what the others counted, and for how long.
 */
function TotalsStamp({ totals }: { totals: IngestTotals | null }) {
  if (totals === null) {
    return <span>totais indisponíveis</span>
  }

  if (!totals.stale) {
    return (
      <span className="flex items-center gap-1.5">
        <StatusDot tone="ok" />
        <span className="uppercase tracking-label">confirmado</span>
        <span className="tnum text-fg-muted">{formatAge(totals.age_seconds * 1000)}</span>
      </span>
    )
  }

  // -1 é o sentinela de "ainda não houve confirmação" — não é erro nem um
  // segundo negativo, e não pode sair na tela como "-1 s atrás".
  const never = totals.age_seconds < 0

  return (
    <span
      role="status"
      className="flex items-center gap-1.5 text-warn"
    >
      <StatusDot tone="warn" />
      <span className="uppercase tracking-label">
        {never ? 'sem confirmação ainda' : 'total parcial'}
      </span>
      <span className="tnum text-fg-muted">
        {never ? NO_VALUE : formatAge(totals.age_seconds * 1000)}
      </span>
    </span>
  )
}

/** Local to this panel: `Metric` takes a formatted string, this takes a number. */
function TotalFigure({
  label,
  value,
  stale,
  monotonic = false,
  unit,
  note,
  size = 'base',
  tone = 'neutral',
}: {
  label: ReactNode
  value: number | null
  stale: boolean
  monotonic?: boolean
  unit?: string
  note?: ReactNode
  size?: 'lg' | 'base'
  tone?: 'neutral' | 'accent'
}) {
  return (
    <div className="min-w-0">
      <span className="block truncate text-micro uppercase tracking-label text-fg-muted">
        {label}
      </span>
      <div
        className={`flex items-baseline gap-1 ${size === 'lg' ? 'text-lg' : 'text-base'} ${
          tone === 'accent' ? 'text-accent' : 'text-fg'
        }`}
      >
        <RollingNumber value={value} stale={stale} monotonic={monotonic} />
        {unit !== undefined && <span className="text-micro font-normal text-fg-muted">{unit}</span>}
      </div>
      {note !== undefined && <div className="mt-0.5 text-micro text-fg-muted">{note}</div>}
    </div>
  )
}

/** Share of the received total, or nothing while there is no denominator. */
function shareNote(part: number | undefined, whole: number | undefined): ReactNode {
  if (part === undefined || whole === undefined || whole === 0) return undefined
  return (
    <>
      <span className="tnum">{formatDecimal(pctOf(part, whole), 2)}%</span> do recebido
    </>
  )
}

/**
 * Cumulative ingestion totals.
 *
 * Sits above the first row because it is the one figure on this screen that
 * answers "how much has this platform actually handled" — every other number
 * here is an instantaneous rate or a window. It stays a strip rather than a
 * hero: the finding this console exists to surface is anomalous silence, and a
 * second 34px number beside it would flatten that hierarchy.
 *
 * The rate under the total comes from `ingest.events_per_sec_received` and is
 * never derived from these counters. Differencing two totals looks like it would
 * work and is wrong on a reconnect, on a restart, and on any tick where `stale`
 * flips.
 */
export function TotalsPanel({
  metrics,
  className,
}: {
  metrics: MetricsPayload | null
  className?: string
}) {
  const totals = metrics?.totals ?? null
  // No totals block means nothing to mark as partial; the stamp says as much.
  const stale = totals?.stale ?? false

  return (
    <Panel title="Eventos ingeridos" className={className} meta={<TotalsStamp totals={totals} />}>
      <div className="grid gap-4 xl:grid-cols-[auto_auto] xl:justify-start xl:gap-6">
        <section className="min-w-0">
          <h3 className="mb-1.5 text-micro uppercase tracking-label text-fg-muted">desde sempre</h3>
          <div className="flex flex-wrap items-start gap-x-6 gap-y-3">
            <TotalFigure
              label="recebidos"
              value={totals?.received ?? null}
              stale={stale}
              monotonic
              size="lg"
              unit="eventos"
              note={
                <>
                  <span className="live-value tnum">
                    {formatInt(metrics?.ingest.events_per_sec_received)}
                  </span>{' '}
                  ev/s agora
                </>
              }
            />
            <TotalFigure
              label="aceitos"
              value={totals?.accepted ?? null}
              stale={stale}
              monotonic
              tone="accent"
              note={shareNote(totals?.accepted, totals?.received)}
            />
            <TotalFigure
              label="rejeitados"
              value={totals?.rejected ?? null}
              stale={stale}
              monotonic
              note={shareNote(totals?.rejected, totals?.received)}
            />
          </div>
        </section>

        <section className="min-w-0 border-t border-line-subtle pt-4 xl:border-t-0 xl:border-l xl:pt-0 xl:pl-6">
          <h3 className="mb-1.5 text-micro uppercase tracking-label text-fg-muted">
            hoje · UTC
          </h3>
          {/* Sem blindagem monotônica aqui de propósito: estes caem a zero na
              virada UTC, e um Math.max prenderia o número de ontem na tela. */}
          <div className="flex flex-wrap items-start gap-x-6 gap-y-3">
            <TotalFigure label="recebidos" value={totals?.today_received ?? null} stale={stale} />
            <TotalFigure
              label="aceitos"
              value={totals?.today_accepted ?? null}
              stale={stale}
              tone="accent"
            />
            <TotalFigure label="rejeitados" value={totals?.today_rejected ?? null} stale={stale} />
          </div>
        </section>
      </div>
    </Panel>
  )
}
